import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileText, RefreshCw, Scale, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';

import { getDocument, getDocumentPdfUrl, getDocumentJsonUrl, getDocumentCsvUrl } from '../lib/api';
import { formatConfidence, formatCurrency, getPriorityClass, getStatusClass } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DocumentReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creatingAppeals, setCreatingAppeals] = useState(false);

  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => getDocument(id),
  });

  const handleCreateAppeals = async () => {
    setCreatingAppeals(true);
    try {
      const response = await fetch(`${API_URL}/api/appeals/from-document/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        if (result.appeals_created > 0) {
          toast.success(`Created ${result.appeals_created} appeal(s) successfully!`);
          queryClient.invalidateQueries(['appeals']);
          // Navigate to appeals page
          navigate('/appeal');
        } else {
          toast.info('No appealable denials found in this document');
        }
      } else {
        toast.error(result.detail || 'Failed to create appeals');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setCreatingAppeals(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Document Not Found</h2>
          <p className="text-muted-foreground mb-6">The requested document could not be found.</p>
          <Link to="/">
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const extractions = doc.extraction_data?.extractions || {};
  const header = extractions.header || {};
  const paymentSummary = extractions.payment_summary || {};
  const claims = extractions.claims || [];
  const serviceLines = extractions.service_lines || [];
  const recoupment = extractions.recoupment || {};
  const denials = doc.extraction_data?.denials || [];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{doc.filename}</h1>
            <div className="flex items-center space-x-2 mt-1">
              <Badge className={getStatusClass(doc.processing_status)}>
                {doc.processing_status}
              </Badge>
              {doc.priority && (
                <Badge className={getPriorityClass(doc.priority)}>
                  {doc.priority}
                </Badge>
              )}
              {doc.classification_type && (
                <span className="text-sm text-muted-foreground">{doc.classification_type}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" asChild data-testid="export-button">
            <a href={getDocumentJsonUrl(id)} download>
              <Download className="w-4 h-4 mr-2" />
              JSON
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="export-button">
            <a href={getDocumentCsvUrl(id)} download>
              <Download className="w-4 h-4 mr-2" />
              CSV
            </a>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PDF Viewer */}
        <Card data-testid="pdf-viewer">
          <CardHeader>
            <CardTitle>Original Document</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted rounded-lg p-4 text-center">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-4">PDF Preview</p>
              <Button variant="outline" asChild>
                <a href={getDocumentPdfUrl(id)} target="_blank" rel="noopener noreferrer">
                  Open PDF
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Extracted Data */}
        <Card>
          <CardHeader>
            <CardTitle>Extracted Data</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="header" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="header">Header</TabsTrigger>
                <TabsTrigger value="payment">Payment</TabsTrigger>
                <TabsTrigger value="claims">Claims</TabsTrigger>
                <TabsTrigger value="service">Services</TabsTrigger>
              </TabsList>

              <TabsContent value="header" className="space-y-3 mt-4">
                <div>
                  <label className="text-xs text-muted-foreground">Check Number</label>
                  <p className="text-sm font-medium">{header.check_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Check Date</label>
                  <p className="text-sm font-medium">{header.check_date || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Provider Name</label>
                  <p className="text-sm font-medium">{header.provider_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Provider ID</label>
                  <p className="text-sm font-medium">{header.provider_id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Payer Name</label>
                  <p className="text-sm font-medium">{header.payer_name || 'N/A'}</p>
                </div>
              </TabsContent>

              <TabsContent value="payment" className="space-y-3 mt-4">
                <div>
                  <label className="text-xs text-muted-foreground">Gross Approved Amount</label>
                  <p className="text-sm font-medium">{formatCurrency(paymentSummary.gross_approved_claim_amount)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Net Amount Due</label>
                  <p className="text-sm font-medium">{formatCurrency(paymentSummary.net_amount_due)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Amount Disbursed</label>
                  <p className="text-sm font-medium">{formatCurrency(paymentSummary.amount_disbursed)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Interest</label>
                  <p className="text-sm font-medium">{formatCurrency(paymentSummary.interest)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">IRS Withheld</label>
                  <p className="text-sm font-medium">{formatCurrency(paymentSummary.irs_withheld)}</p>
                </div>
              </TabsContent>

              <TabsContent value="claims" className="mt-4">
                {claims.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No claims found</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {claims.length} claim{claims.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ScrollArea className="h-[350px]">
                      <div className="space-y-3 pr-4">
                        {claims.map((claim, idx) => (
                          <Card key={idx} className="bg-muted/30">
                            <CardContent className="pt-4 space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="text-xs text-muted-foreground">Claim Number</label>
                                  <p className="text-sm font-medium font-mono">{claim.claim_number}</p>
                                </div>
                                {claim.denial_flag && (
                                  <Badge variant="destructive" className="text-xs">Denied</Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-muted-foreground">Patient Name</label>
                                  <p className="text-sm font-medium">{claim.patient_name || 'N/A'}</p>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Insured Name</label>
                                  <p className="text-sm font-medium">{claim.insured_name || 'N/A'}</p>
                                </div>
                              </div>
                              {(claim.date_of_service || claim.total_amount) && (
                                <div className="grid grid-cols-2 gap-2">
                                  {claim.date_of_service && (
                                    <div>
                                      <label className="text-xs text-muted-foreground">Date of Service</label>
                                      <p className="text-sm">{claim.date_of_service}</p>
                                    </div>
                                  )}
                                  {claim.total_amount && (
                                    <div>
                                      <label className="text-xs text-muted-foreground">Amount</label>
                                      <p className="text-sm font-medium">{formatCurrency(claim.total_amount)}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="service" className="mt-4">
                {serviceLines.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No service lines found</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {serviceLines.length} service line{serviceLines.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ScrollArea className="h-[350px] rounded-md border">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-[100px]">Code</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Charge</TableHead>
                            <TableHead className="text-right">Allowed</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead>Exp. Code</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {serviceLines.map((sl, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/30">
                              <TableCell className="font-mono text-xs font-medium">{sl.service_code || 'N/A'}</TableCell>
                              <TableCell className="text-xs max-w-[150px] truncate" title={sl.description}>
                                {sl.description || '-'}
                              </TableCell>
                              <TableCell className="text-xs text-right">{formatCurrency(sl.charge_amount)}</TableCell>
                              <TableCell className="text-xs text-right">{formatCurrency(sl.allowed_amount)}</TableCell>
                              <TableCell className="text-xs text-right font-medium">{formatCurrency(sl.what_we_will_pay || sl.paid_amount)}</TableCell>
                              <TableCell className="text-xs font-mono">
                                {sl.explanation_codes?.length > 0 ? (
                                  <Badge variant="outline" className="text-xs">
                                    {sl.explanation_codes.join(', ')}
                                  </Badge>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                    {/* Service Line Totals */}
                    <div className="flex justify-end gap-6 pt-2 border-t text-sm">
                      <div>
                        <span className="text-muted-foreground">Total Charged: </span>
                        <span className="font-medium">{formatCurrency(serviceLines.reduce((sum, sl) => sum + (sl.charge_amount || 0), 0))}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Paid: </span>
                        <span className="font-medium text-emerald-600">{formatCurrency(serviceLines.reduce((sum, sl) => sum + (sl.what_we_will_pay || sl.paid_amount || 0), 0))}</span>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Denials Section */}
      {denials.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-amber-600" />
                  Detected Denials
                </CardTitle>
                <CardDescription>
                  {denials.length} denial{denials.length !== 1 ? 's' : ''} detected - eligible for appeal
                </CardDescription>
              </div>
              <Button 
                onClick={handleCreateAppeals}
                disabled={creatingAppeals}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="create-appeals-btn"
              >
                {creatingAppeals ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating Appeals...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Appeals
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-100/50">
                    <TableHead>Claim</TableHead>
                    <TableHead>Service Code</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Appealable</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {denials.map((denial, idx) => (
                    <TableRow key={idx} className="hover:bg-amber-50">
                      <TableCell className="font-mono text-xs">{denial.claim_number}</TableCell>
                      <TableCell className="font-mono text-xs">{denial.service_code}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {denial.denial_category || denial.category || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{denial.explanation_code || denial.code}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{formatCurrency(denial.amount_affected || denial.amount)}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          Yes
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            
            {/* Total Denied Amount */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-amber-200">
              <div className="text-sm text-muted-foreground">
                Total denied amount at risk
              </div>
              <div className="text-xl font-bold text-amber-700">
                {formatCurrency(denials.reduce((sum, d) => sum + (d.amount_affected || d.amount || 0), 0))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
